export type ToastVariant = 'success' | 'error' | 'info'

export type ToastInput = {
  title: string
  description?: string
  durationMs?: number
  variant?: ToastVariant
}

export type ToastItem = {
  id: number
  title: string
  description?: string
  durationMs: number
  variant: ToastVariant
  state: 'open' | 'closing'
}

type Listener = () => void

const DEFAULT_DURATION_MS = 3500
const EXIT_DURATION_MS = 200

let nextId = 1
let items: ToastItem[] = []

const listeners = new Set<Listener>()
const closeTimers = new Map<number, number>()
const removeTimers = new Map<number, number>()

function emit() {
  listeners.forEach((listener) => listener())
}

function scheduleClose(id: number, durationMs: number) {
  const timer = window.setTimeout(() => {
    startClose(id)
  }, durationMs)
  closeTimers.set(id, timer)
}

function clearCloseTimer(id: number) {
  const timer = closeTimers.get(id)
  if (typeof timer === 'number') {
    window.clearTimeout(timer)
    closeTimers.delete(id)
  }
}

function clearRemoveTimer(id: number) {
  const timer = removeTimers.get(id)
  if (typeof timer === 'number') {
    window.clearTimeout(timer)
    removeTimers.delete(id)
  }
}

function scheduleRemoval(id: number) {
  const timer = window.setTimeout(() => {
    const previousLength = items.length
    items = items.filter((item) => item.id !== id)
    if (items.length === previousLength) {
      return
    }
    clearCloseTimer(id)
    clearRemoveTimer(id)
    emit()
  }, EXIT_DURATION_MS)
  removeTimers.set(id, timer)
}

export function startClose(id: number) {
  const toast = items.find((item) => item.id === id)
  if (!toast || toast.state === 'closing') {
    return
  }
  items = items.map((item) => (item.id === id ? { ...item, state: 'closing' } : item))
  clearCloseTimer(id)
  clearRemoveTimer(id)
  scheduleRemoval(id)
  emit()
}

function normalize(input: ToastInput): ToastItem {
  return {
    id: nextId++,
    title: input.title,
    description: input.description,
    durationMs: input.durationMs ?? DEFAULT_DURATION_MS,
    variant: input.variant ?? 'info',
    state: 'open',
  }
}

export function getToastSnapshot() {
  return items
}

export function subscribeToToasts(listener: Listener) {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function showToast(input: ToastInput) {
  const item = normalize(input)
  items = [...items, item]
  emit()
  scheduleClose(item.id, item.durationMs)
  return item.id
}

export function remove(id: number) {
  startClose(id)
}

export function clearToasts() {
  items.forEach((item) => {
    clearCloseTimer(item.id)
    clearRemoveTimer(item.id)
  })
  items = []
  emit()
}

export const toast = {
  show: showToast,
  success(input: Omit<ToastInput, 'variant'>) {
    return showToast({ ...input, variant: 'success' })
  },
  error(input: Omit<ToastInput, 'variant'>) {
    return showToast({ ...input, variant: 'error' })
  },
  info(input: Omit<ToastInput, 'variant'>) {
    return showToast({ ...input, variant: 'info' })
  },
  dismiss: remove,
  clear: clearToasts,
}
